import { create } from 'zustand';
import { UserProfile, VacationDay, DailyEntry, CalculationResults, DailyTarget } from '../types';
import { TargetSummary, TargetException } from '../types/optimizedTargets';
import supabase from '../services/supabase';
import { parseISO, differenceInMinutes } from 'date-fns';
import { calculateTargetSummary, generateTargetExceptions } from '../utils/optimizedCalculations';

// Define UserState interface explicitly to resolve the reference
interface UserState {
  profile: UserProfile | null;
  vacationDays: VacationDay[];
  dailyEntries: DailyEntry[];
  dailyTargets: DailyTarget[];
  calculations: CalculationResults | null;
  // isLoading: boolean; // Keep for general actions like profile update?
  error: string | null;

  // Granular loading states
  profileLoading: boolean;
  vacationLoading: boolean;
  entriesLoading: boolean;
  targetsLoading: boolean;
  summaryLoading: boolean;
  exceptionsLoading: boolean;
  
  // Actions
  fetchUserProfile: (forceRefresh?: boolean) => Promise<void>; // Add optional forceRefresh parameter
  fetchVacationDays: () => Promise<void>;
  fetchDailyEntries: () => Promise<void>;
  fetchDailyTargets: () => Promise<void>;
  createProfile: (profile: Partial<UserProfile>) => Promise<void>;
  updateProfile: (profile: Partial<UserProfile>) => Promise<void>;
  addVacationDay: (date: string) => Promise<void>;
  removeVacationDay: (id: string) => Promise<void>;
  batchUpdateVacationDays: (dates: string[]) => Promise<void>;
  logDailyHours: (date: string, hours: number) => Promise<void>;
  editDailyHours: (id: string, hours: number) => Promise<void>;
  deleteDailyHours: (id: string) => Promise<void>;
  calculateTargets: () => void;
  refreshDailyTargets: () => Promise<void>;
}

// Cache expiry times in minutes
const CACHE_EXPIRY = {
  PROFILE: 5,          // Profile data expires after 5 minutes
  VACATION_DAYS: 10,   // Vacation days expire after 10 minutes  
  DAILY_ENTRIES: 2,    // Daily entries expire after 2 minutes (more frequent updates)
  TARGET_SUMMARY: 5,   // Target summary expires after 5 minutes
  EXCEPTIONS: 10       // Exceptions expire after 10 minutes
};

// Cache interface for storing data with timestamps
interface CachedData<T> {
  data: T;
  timestamp: Date;
  isValid: boolean;
}

interface OptimizedUserState extends Omit<UserState, 'refreshDailyTargets'> {
  targetSummary: TargetSummary | null;
  targetExceptions: TargetException[];
  refreshDailyTargets: () => Promise<void>;
  fetchTargetSummary: (forceRefresh?: boolean) => Promise<void>;
  refreshTargetSummary: (preferCache?: boolean) => Promise<void>;
  fetchTargetExceptions: (forceRefresh?: boolean) => Promise<void>;
  refreshTargetExceptions: () => Promise<void>;
  
  // Cache metadata
  profileCache: CachedData<UserProfile | null> | null;
  vacationDaysCache: CachedData<VacationDay[]> | null;
  dailyEntriesCache: CachedData<DailyEntry[]> | null;
  targetSummaryCache: CachedData<TargetSummary | null> | null;
  exceptionsCache: CachedData<TargetException[]> | null;
}

// Helper function to check if cached data is still valid
const isCacheValid = <T>(cache: CachedData<T> | null, expiryMinutes: number): boolean => {
  if (!cache || !cache.isValid) return false;
  
  const now = new Date();
  const minutesSinceUpdate = differenceInMinutes(now, cache.timestamp);
  return minutesSinceUpdate < expiryMinutes;
};

// Helper function for updating state with optimistic data
const updateStateWithOptimisticData = (
  set: any, 
  get: any,
  entries: DailyEntry[],
  calculations: any
) => {
  // Create an optimistic target summary to keep stats in sync
  const profile = get().profile;
  const currentTargetSummary = get().targetSummary;
  
  // Calculate the total billed hours directly from entries
  const totalBilledHours = entries.reduce(
    (sum: number, entry: DailyEntry) => sum + entry.billed_hours, 
    0
  );
  
  // Always update calculations for charts
  let updates: {
    dailyEntries: DailyEntry[],
    dailyEntriesCache: CachedData<DailyEntry[]>,
    calculations: CalculationResults | null,
    error: null,
    targetSummary?: TargetSummary,
    targetSummaryCache?: CachedData<TargetSummary | null>
  } = {
    dailyEntries: entries,
    dailyEntriesCache: {
      data: entries,
      timestamp: new Date(),
      isValid: true
    },
    calculations: calculations ? {
      totalWorkDays: calculations.totalWorkDays || 0,
      netAvailableWorkDays: calculations.netAvailableWorkDays || 0,
      remainingWorkDays: calculations.remainingWorkDays || 0,
      requiredAverageDailyHours: calculations.requiredAverageDailyHours || 0,
      requiredDailyHoursForToday: calculations.requiredDailyHoursForToday || 0,
      totalHoursBilled: totalBilledHours, // Ensure this is consistent
      remainingHoursNeeded: calculations.remainingHoursNeeded || 0,
      progressPercentage: calculations.progressPercentage || 0
    } : null,
    error: null
  };
  
  // If we have a current target summary, update it with optimistic values
  if (currentTargetSummary && calculations && profile) {
    // Calculate optimistically updated remaining hours
    const remainingHours = profile.yearly_target_hours - totalBilledHours;

    // Calculate projected completion date optimistically
    let projectedCompletionDate = currentTargetSummary.projected_completion_date;
    
    // Update the projected completion date based on the current progress
    if (profile.yearly_target_hours > 0) {
      // If target is already met, use today's date
      if (remainingHours <= 0) {
        projectedCompletionDate = new Date().toISOString().split('T')[0];
      } 
      // Otherwise, recalculate based on current rates
      else if (totalBilledHours > 0 && calculations.remainingWorkDays > 0) {
        // Calculate full target summary to get accurate projected date
        const optimisticSummary = calculateTargetSummary(
          profile.tracking_start_date,
          profile.tracking_end_date,
          profile.work_week_days,
          profile.yearly_target_hours,
          get().vacationDays,
          entries,
          profile.id
        );
        projectedCompletionDate = optimisticSummary.projected_completion_date;
      }
    }

    const optimisticTargetSummary = {
      ...currentTargetSummary,
      // Ensure these key fields are updated for stats display
      remaining_hours: remainingHours,
      total_billed_hours: totalBilledHours, 
      remaining_work_days: calculations.remainingWorkDays,
      projected_completion_date: projectedCompletionDate, // Update projected date
      last_calculated_at: new Date().toISOString()
    };
    
    updates = {
      ...updates,
      targetSummary: optimisticTargetSummary,
      targetSummaryCache: {
        data: optimisticTargetSummary,
        timestamp: new Date(),
        isValid: true
      }
    };
  }
  
  // Update all related state at once to prevent UI flicker/inconsistency
  set(updates);
};

// Define our optimized user store
export const useOptimizedUserStore = create<OptimizedUserState>((set, get) => ({
  // Data state
  profile: null,
  vacationDays: [],
  dailyEntries: [],
  dailyTargets: [],
  targetSummary: null,
  targetExceptions: [],
  calculations: null,
  // isLoading: false, // Initial state for general loading
  error: null,

  // Granular loading states initial values
  profileLoading: false,
  vacationLoading: false,
  entriesLoading: false,
  targetsLoading: false,
  summaryLoading: false,
  exceptionsLoading: false,
  
  // Cache state
  profileCache: null,
  vacationDaysCache: null,
  dailyEntriesCache: null,
  targetSummaryCache: null,
  exceptionsCache: null,

  // Enhanced methods with caching
  fetchUserProfile: async (forceRefresh = false) => {
    // Prevent concurrent fetches
    if (get().profileLoading) return;

    // Check cache first
    const profileCache = get().profileCache;
    if (!forceRefresh && isCacheValid(profileCache, CACHE_EXPIRY.PROFILE)) {
      set({ profile: profileCache!.data, error: null }); // Don't set loading false here
      console.log('Using cached profile data');
      get().calculateTargets();
      // Fetch summary if profile is loaded from cache
      if (profileCache!.data) {
        await get().fetchTargetSummary();
      }
      return;
    }
    
    set({ profileLoading: true, error: null });
    try {
      const { data: session } = await supabase.auth.getSession();
      
      if (!session.session) {
        set({ error: 'No active session' });
        return;
      }
      
      const userId = session.session.user.id;
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();
        
      // Handle "not found" errors differently than other errors
      // PostgreREST error code PGRST116 means "no rows returned by a query that expected to return at least one row"
      if (error && error.code === 'PGRST116') {
        // Profile not found - this is expected for new users
        console.log('No profile found for user, they need to complete setup wizard');
        set({ 
          profile: null, 
          profileCache: {
            data: null,
            timestamp: new Date(),
            isValid: true
          },
          error: null // Don't set an error as this is an expected state
        });
        return; // Return added here
      } else if (error) {
        throw error;
      }
      
      // Update both the state and cache
      const newCacheEntry = {
        data: data as UserProfile,
        timestamp: new Date(),
        isValid: true
      };
      
      set({ 
        profile: data as UserProfile, 
        profileCache: newCacheEntry,
        error: null 
      });
      
      get().calculateTargets();
      
      // Also fetch target summary
      await get().fetchTargetSummary();
    } catch (error: any) {
      set({ error: error.message });
    } finally {
      set({ profileLoading: false }); // Ensure loading is false on exit
    }
  },
  
  fetchVacationDays: async (forceRefresh = false) => {
    // Prevent concurrent fetches
    if (get().vacationLoading) return;

    // Check cache first
    const vacationDaysCache = get().vacationDaysCache;
    if (!forceRefresh && isCacheValid(vacationDaysCache, CACHE_EXPIRY.VACATION_DAYS)) {
      set({ vacationDays: vacationDaysCache!.data, error: null });
      console.log('Using cached vacation days data');
      get().calculateTargets();
      return;
    }
    
    set({ vacationLoading: true, error: null });
    try {
      const { data: session } = await supabase.auth.getSession();
      
      if (!session.session) {
        set({ error: 'No active session' });
        return;
      }
      
      const userId = session.session.user.id;
      const { data, error } = await supabase
        .from('vacation_days')
        .select('*')
        .eq('profile_id', userId);
        
      if (error) throw error;
      
      // Update both the state and cache
      const newCacheEntry = {
        data: data as VacationDay[],
        timestamp: new Date(),
        isValid: true
      };
      
      set({ 
        vacationDays: data as VacationDay[], 
        vacationDaysCache: newCacheEntry,
        error: null 
      });
      
      get().calculateTargets();
      
      // After vacation days update, refresh exceptions and targets
      await get().refreshTargetExceptions(); // Keep this for now
    } catch (error: any) {
      set({ error: error.message });
    } finally {
      set({ vacationLoading: false });
    }
  },
  
  fetchDailyEntries: async (forceRefresh = false) => {
    // Prevent concurrent fetches
    if (get().entriesLoading) return;

    // Check cache first
    const dailyEntriesCache = get().dailyEntriesCache;
    if (!forceRefresh && isCacheValid(dailyEntriesCache, CACHE_EXPIRY.DAILY_ENTRIES)) {
      set({ dailyEntries: dailyEntriesCache!.data, error: null });
      console.log('Using cached daily entries data');
      get().calculateTargets();
      return;
    }
    
    set({ entriesLoading: true, error: null });
    try {
      const { data: session } = await supabase.auth.getSession();
      
      if (!session.session) {
        set({ error: 'No active session' });
        return;
      }
      
      const userId = session.session.user.id;
      const { data, error } = await supabase
        .from('daily_entries')
        .select('*')
        .eq('profile_id', userId);
        
      if (error) throw error;
      
      // Update both the state and cache
      const newCacheEntry = {
        data: data as DailyEntry[],
        timestamp: new Date(),
        isValid: true
      };
      
      set({ 
        dailyEntries: data as DailyEntry[], 
        dailyEntriesCache: newCacheEntry,
        error: null 
      });
      
      get().calculateTargets();
      
      // After entries update, refresh the target summary
      await get().refreshTargetSummary(); // Keep this for now
    } catch (error: any) {
      set({ error: error.message });
    } finally {
      set({ entriesLoading: false });
    }
  },
  
  // Fetch daily targets from the view
  fetchDailyTargets: async () => {
    // Prevent concurrent fetches
    if (get().targetsLoading) return;

    set({ targetsLoading: true, error: null });
    try {
      const { data: session } = await supabase.auth.getSession();
      
      if (!session.session) {
        set({ error: 'No active session' });
        return;
      }
      
      const userId = session.session.user.id;
      const { data, error } = await supabase
        .from('daily_targets')
        .select('*')
        .eq('profile_id', userId);
        
      if (error) throw error;
      
      set({ dailyTargets: data as DailyTarget[] });
    } catch (error: any) {
      set({ error: error.message });
    } finally {
      set({ targetsLoading: false });
    }
  },
  
  // New method for fetching the target summary
  fetchTargetSummary: async (forceRefresh = false) => {
    // Prevent concurrent fetches
    if (get().summaryLoading) return;

    // Check cache first
    const targetSummaryCache = get().targetSummaryCache;
    if (!forceRefresh && isCacheValid(targetSummaryCache, CACHE_EXPIRY.TARGET_SUMMARY)) {
      set({ targetSummary: targetSummaryCache!.data });
      console.log('Using cached target summary data');
      return;
    }
    
    set({ summaryLoading: true, error: null }); // Set loading true
    try {
      const { data: session } = await supabase.auth.getSession();
      
      if (!session.session) {
        return;
      }
      
      const userId = session.session.user.id;
      
      // Add explicit headers to fix the 406 Not Acceptable error
      const { data, error } = await supabase
        .from('target_summaries')
        .select('*')
        .eq('profile_id', userId)
        .single()
        .returns<TargetSummary | null>();
        
      if (error && error.code !== 'PGRST116') throw error; // Not found is OK
      
      // Update both the state and cache
      const newCacheEntry = {
        data: data as TargetSummary || null,
        timestamp: new Date(),
        isValid: true
      };
      
      set({ 
        targetSummary: data as TargetSummary || null,
        targetSummaryCache: newCacheEntry
      });
    } catch (error: any) {
      console.error('Error fetching target summary:', error);
      set({ error: error.message }); // Set error state
    } finally {
      set({ summaryLoading: false }); // Set loading false
    }
  },
  
  // New method for fetching exceptions
  fetchTargetExceptions: async (forceRefresh = false) => {
    // Prevent concurrent fetches
    if (get().exceptionsLoading) return;

    // Check cache first
    const exceptionsCache = get().exceptionsCache;
    if (!forceRefresh && isCacheValid(exceptionsCache, CACHE_EXPIRY.EXCEPTIONS)) {
      set({ targetExceptions: exceptionsCache!.data });
      console.log('Using cached target exceptions data');
      return;
    }
    
    set({ exceptionsLoading: true, error: null }); // Set loading true
    try {
      const { data: session } = await supabase.auth.getSession();
      
      if (!session.session) {
        return;
      }
      
      const userId = session.session.user.id;
      const { data, error } = await supabase
        .from('target_exceptions')
        .select('*')
        .eq('profile_id', userId);
        
      if (error) throw error;
      
      // Update both the state and cache
      const newCacheEntry = {
        data: data as TargetException[] || [],
        timestamp: new Date(),
        isValid: true
      };
      
      set({ 
        targetExceptions: data as TargetException[] || [],
        exceptionsCache: newCacheEntry
      });
    } catch (error: any) {
      console.error('Error fetching target exceptions:', error);
      set({ error: error.message }); // Set error state
    } finally {
      set({ exceptionsLoading: false }); // Set loading false
    }
  },
  
  // Create new user profile - will trigger refreshTargetSummary
  createProfile: async (profileData: Partial<UserProfile>) => {
    set({ error: null });
    try {
      const { data: session } = await supabase.auth.getSession();
      
      if (!session.session) {
        set({ error: 'No active session' });
        return;
      }
      
      const userId = session.session.user.id;
      const { data, error } = await supabase
        .from('profiles')
        .insert({
          id: userId,
          ...profileData,
          setup_completed: profileData.setup_completed !== undefined ? profileData.setup_completed : false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .select()
        .single();
        
      if (error) throw error;
      
      set({ 
        profile: data as UserProfile,
        error: null 
      });
      get().calculateTargets();
      
      // Initialize target summary
      await get().refreshTargetSummary();
      await get().refreshTargetExceptions();
    } catch (error: any) {
      set({ error: error.message });
    }
  },
  
  // Update user profile
  updateProfile: async (profileUpdate: Partial<UserProfile>) => {
    set({ error: null });
    try {
      const { data: session } = await supabase.auth.getSession();
      
      if (!session.session) {
        set({ error: 'No active session' });
        return;
      }
      
      const userId = session.session.user.id;
      console.log('Updating profile for user ID:', userId);
      
      // First check if profile exists
      const { error: fetchError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();
        
      if (fetchError && fetchError.code === 'PGRST116') {
        // Profile doesn't exist, so create it instead
        console.log('Profile does not exist, creating new profile instead');
        return get().createProfile(profileUpdate);
      } else if (fetchError) {
        console.error('Error checking if profile exists:', fetchError);
        throw fetchError;
      }
      
      console.log('Sending profile update with data:', {
        ...profileUpdate,
        updated_at: new Date().toISOString(),
      });
      
      // Profile exists, so update it
      const { error } = await supabase
        .from('profiles')
        .update({
          ...profileUpdate,
          updated_at: new Date().toISOString(),
        })
        .eq('id', userId); // Remove .select().single() from here
        
      if (error) {
        console.error('Error updating profile:', error);
        throw error;
      }
      
      // Manually fetch the profile again after successful update
      console.log('Profile update successful, manually refetching profile...');
      await get().fetchUserProfile(true); // Force refresh to bypass cache

      // Since fetchUserProfile updates the state and cache, we might not need to set it manually here.
      // However, ensure isLoading is false if fetchUserProfile doesn't handle it in all paths.
      // fetchUserProfile already sets isLoading to false on success/error, so this might be redundant.
      // set({ isLoading: false }); 
      
      console.log('Local state should now be updated with profile.');
      
      // Recalculate targets based on the updated profile (fetchUserProfile might already trigger this)
      // get().calculateTargets(); // This might be redundant if fetchUserProfile calls it

      try {
        // Refresh target summary in the background
        console.log('Refreshing target summary after profile update');
        await get().refreshTargetSummary(); 
      } catch (summaryError: any) {
        console.error('Error refreshing target summary:', summaryError);
      }
    } catch (error: any) {
      console.error('Profile update failed with error:', error);
      set({ error: error.message });
    }
  },
  
  // Add vacation day
  addVacationDay: async (date: string) => {
    set({ error: null });
    try {
      const { data: session } = await supabase.auth.getSession();
      
      if (!session.session) {
        set({ error: 'No active session' });
        return;
      }
      
      const userId = session.session.user.id;
      
      // Check if this vacation day already exists
      const isDuplicate = get().vacationDays.some(
        (vd: VacationDay) => vd.vacation_date === date
      );
      
      if (isDuplicate) {
        set({ error: 'This date is already marked as vacation' });
        return;
      }
      
      const { data, error } = await supabase
        .from('vacation_days')
        .insert({
          profile_id: userId,
          vacation_date: date
        })
        .select()
        .single();
        
      if (error) throw error;
      
      set({ 
        vacationDays: [...get().vacationDays, data as VacationDay],
        error: null 
      });
      get().calculateTargets();
      
      // Try to add as target exception but don't fail if it doesn't work
      try {
        await supabase
          .from('target_exceptions')
          .upsert({
            profile_id: userId,
            exception_date: date,
            target_hours: 0,
            is_work_day: false,
            is_vacation_day: true,
            reason: 'vacation'
          });
      } catch (excError) {
        console.warn('Non-critical error adding target exception:', excError);
      }
        
      // Refresh target summary
      await get().refreshTargetSummary();
      
      // Try to fetch exceptions but don't fail if it doesn't work
      try {
        await get().fetchTargetExceptions();
      } catch (excFetchError) {
        console.warn('Non-critical error fetching target exceptions:', excFetchError);
      }
    } catch (error: any) {
      set({ error: error.message });
    }
  },
  
  // Remove vacation day
  removeVacationDay: async (id: string) => {
    set({ error: null });
    try {
      // First get the vacation date
      const vacDay = get().vacationDays.find((vd: VacationDay) => vd.id === id);
      if (!vacDay) throw new Error('Vacation day not found');
      
      const { error } = await supabase
        .from('vacation_days')
        .delete()
        .eq('id', id);
        
      if (error) throw error;
      
      set({ 
        vacationDays: get().vacationDays.filter((vd: VacationDay) => vd.id !== id),
        error: null 
      });
      
      // Also remove from target exceptions
      await supabase
        .from('target_exceptions')
        .delete()
        .eq('profile_id', vacDay.profile_id)
        .eq('exception_date', vacDay.vacation_date)
        .eq('reason', 'vacation');
      
      get().calculateTargets();
      
      // Refresh targets
      await get().refreshTargetSummary();
      await get().fetchTargetExceptions();
    } catch (error: any) {
      set({ error: error.message });
    }
  },
  
  // Batch update vacation days (optimized version)
  batchUpdateVacationDays: async (dates: string[]) => {
    set({ error: null });
    try {
      const { data: session } = await supabase.auth.getSession();
      
      if (!session.session) {
        set({ error: 'No active session' });
        return;
      }
      
      const userId = session.session.user.id;
      
      // First delete all existing vacation days in a single operation
      const { error: deleteError } = await supabase
        .from('vacation_days')
        .delete()
        .eq('profile_id', userId);
        
      if (deleteError) throw deleteError;
      
      // Try to delete exceptions, but don't fail if there's an error
      try {
        // Delete all vacation exceptions
        await supabase
          .from('target_exceptions')
          .delete()
          .eq('profile_id', userId)
          .eq('reason', 'vacation');
      } catch (excDeleteError) {
        console.warn('Non-critical error deleting target exceptions:', excDeleteError);
      }
      
      // If no dates to add, we're done
      if (dates.length === 0) {
        set({ vacationDays: [] });
        get().calculateTargets();
        await get().refreshTargetSummary();
        await get().fetchTargetExceptions();
        return;
      }
      
      // Prepare batch insert data for vacation_days
      const vacationData = dates.map((date: string) => ({
        profile_id: userId,
        vacation_date: date
      }));
      
      // Prepare batch insert data for target_exceptions
      const exceptionData = dates.map((date: string) => ({
        profile_id: userId,
        exception_date: date,
        target_hours: 0,
        is_work_day: false, 
        is_vacation_day: true,
        reason: 'vacation'
      }));
      
      // Insert all new vacation days in a single batch operation
      const { data, error: insertError } = await supabase
        .from('vacation_days')
        .insert(vacationData)
        .select();
        
      if (insertError) throw insertError;
      
      // Try to upsert exceptions instead of inserting them
      try {
        // Use upsert for all new vacation exceptions
        await supabase
          .from('target_exceptions')
          .upsert(exceptionData, {
            onConflict: 'profile_id,exception_date',
            ignoreDuplicates: false
          });
      } catch (excUpsertError) {
        console.warn('Non-critical error upserting target exceptions:', excUpsertError);
      }
      
      set({ 
        vacationDays: data as VacationDay[],
        error: null 
      });
      
      get().calculateTargets();
      await get().refreshTargetSummary();
      
      // Try to fetch exceptions, but don't fail if there's an error
      try {
        await get().fetchTargetExceptions();
      } catch (excFetchError) {
        console.warn('Non-critical error fetching target exceptions:', excFetchError);
      }
    } catch (error: any) {
      set({ error: error.message });
    }
  },
  
  // Log daily hours with optimistic updates
  logDailyHours: async (date: string, hours: number) => {
    // We're using a fully optimistic approach with no loading state
    // This keeps the UI responsive and prevents spinner on buttons
    
    try {
      // Get user ID from existing profile if available
      let userId = get().profile?.id;
      
      // If we don't have a user ID, we need to fetch the session
      if (!userId) {
        const { data: session } = await supabase.auth.getSession();
        
        if (!session?.session) {
          set({ error: 'No active session' });
          return;
        }
        
        userId = session.session.user.id;
      }
      
      // Check if an entry for this date already exists
      const existingEntry = get().dailyEntries.find(
        (entry: DailyEntry) => entry.entry_date === date
      );
      
      if (existingEntry) {
        // Update existing entry using our optimistic edit method
        return get().editDailyHours(existingEntry.id, hours);
      }
      
      // Create an optimistic entry with a temporary ID
      const tempId = `temp-${Date.now()}`;
      const optimisticEntry: DailyEntry = {
        id: tempId,
        profile_id: userId,
        entry_date: date,
        billed_hours: hours,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      
      // Create a snapshot of calculations before update for proper optimistic UI
      const preUpdateCalculations = { ...get().calculations };
      
      // Update state with optimistic data
      const updatedEntries = [...get().dailyEntries, optimisticEntry];
      
      // Calculate targets immediately with optimistic data
      get().calculateTargets();
      
      // Keep a reference to our optimistic calculations
      const optimisticCalculations = { ...get().calculations } as CalculationResults;
      
      // Use our helper to update all related state consistently
      updateStateWithOptimisticData(set, get, updatedEntries, optimisticCalculations);
      
      // Perform database operation in the background without blocking UI
      try {
        const { data, error } = await supabase
          .from('daily_entries')
          .insert({
            profile_id: userId,
            entry_date: date,
            billed_hours: hours
          })
          .select()
          .single();
          
        if (error) {
          console.error('Error adding entry:', error);
            
          // On error, remove the optimistic entry
          const revertedEntries = get().dailyEntries.filter((e: DailyEntry) => e.id !== tempId);
            
          set({ 
            dailyEntries: revertedEntries,
            dailyEntriesCache: {
              data: revertedEntries,
              timestamp: new Date(),
              isValid: true
            },
            calculations: preUpdateCalculations as CalculationResults, // Restore original calculations
            error: error.message
          });
        } else {
          // Replace the temporary entry with the real one from the database
          const finalEntries = get().dailyEntries.map((entry: DailyEntry) => 
            entry.id === tempId ? data as DailyEntry : entry
          );
            
          const newCacheEntry = {
            data: finalEntries,
            timestamp: new Date(),
            isValid: true
          };
            
          set({
            dailyEntries: finalEntries,
            dailyEntriesCache: newCacheEntry,              // Keep our optimistic calculations
              calculations: optimisticCalculations as CalculationResults
          });
            
          // Background refresh targets without loading state
          try {
            const profile = get().profile;
              
            if (profile) {
              // Update server-side target summary but keep our optimistic UI
              const summary = calculateTargetSummary(
                profile.tracking_start_date,
                profile.tracking_end_date,
                profile.work_week_days,
                profile.yearly_target_hours,
                get().vacationDays,
                finalEntries,
                userId
              );
                
              await supabase
                .from('target_summaries')
                .upsert(summary, {
                  onConflict: 'profile_id',
                  ignoreDuplicates: false
                });
                
              // Update cache timestamp but keep our optimistic calculations
              set({
                targetSummaryCache: {
                  data: get().targetSummary || null,
                  timestamp: new Date(),
                  isValid: true
                },
                // Ensure we keep our optimistic calculations
                calculations: optimisticCalculations
              });
            }
          } catch (summaryError) {
            // Non-fatal error - UI remains responsive with optimistic data
            console.warn('Background target summary update error:', summaryError);
          }
        }
      } catch (dbError) {
        console.error('Database operation failed:', dbError);
        // Even on error, UI remains responsive
      }
    } catch (error: any) {
      set({ error: error.message });
    }
  },
  
  // Edit daily hours with optimistic updates - completely revised implementation
  editDailyHours: async (id: string, hours: number) => {
    // IMPORTANT: Never set isLoading to true for optimistic updates
    
    // Find the entry to update
    const entryToUpdate = get().dailyEntries.find((entry: DailyEntry) => entry.id === id);
    if (!entryToUpdate) {
      set({ error: 'Entry not found' });
      return;
    }
    
    // Create optimistic update with current timestamp
    const optimisticEntry = {
      ...entryToUpdate,
      billed_hours: hours,
      updated_at: new Date().toISOString()
    };
    
    // Update state and cache optimistically (before DB operation)
    const updatedEntries = get().dailyEntries.map((entry: DailyEntry) => 
      entry.id === id ? optimisticEntry : entry
    );
    
    // Create a snapshot of calculations before update for proper optimistic UI
    const preUpdateCalculations = { ...get().calculations };
    
    // Update cache with optimistic data - this is key to preventing reverts
    const newCacheEntry = {
      data: updatedEntries,
      timestamp: new Date(),
      isValid: true
    };
    
    // Do local calculations without waiting for server
    get().calculateTargets();
    
    // Keep a reference to our optimistic calculations 
    const optimisticCalculations = { ...get().calculations } as CalculationResults;
    
    // Use our helper to update all related state consistently
    updateStateWithOptimisticData(set, get, updatedEntries, optimisticCalculations);
    
    // Start background DB operation without blocking the UI
    try {
      // Use async/await but don't block UI
      // We're not setting isLoading so this runs in background
      const { error } = await supabase
        .from('daily_entries')
        .update({
          billed_hours: hours,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();
      
      if (error) {
        console.error('Error updating entry:', error);
        
        // On error, revert to previous state
        const revertedEntries = get().dailyEntries.map((entry: DailyEntry) => 
          entry.id === id ? entryToUpdate : entry
        );
        
        set({ 
          dailyEntries: revertedEntries,
          dailyEntriesCache: { ...newCacheEntry, data: revertedEntries },
          calculations: preUpdateCalculations as CalculationResults, // Restore original calculations
          error: error.message
        });
      } else {
        // Silently update with real data from DB if needed
        
        // Background refresh targets without loading state
        try {
          const { data: session } = await supabase.auth.getSession();
          if (session?.session) {
            const userId = session.session.user.id;
            const profile = get().profile;
            
            if (profile) {
              // Update server-side target summary but keep our optimistic UI
              // This ensures database consistency without UI reverts
              const summary = calculateTargetSummary(
                profile.tracking_start_date,
                profile.tracking_end_date,
                profile.work_week_days,
                profile.yearly_target_hours,
                get().vacationDays,
                updatedEntries,
                userId
              );
              
              await supabase
                .from('target_summaries')
                .upsert(summary, {
                  onConflict: 'profile_id',
                  ignoreDuplicates: false
                });
              
              // Update cache timestamp but keep our optimistic calculations
              set({
                targetSummaryCache: {
                  data: get().targetSummary || null,
                  timestamp: new Date(),
                  isValid: true
                },
                // Ensure we keep our optimistic calculations instead of reverting
                calculations: optimisticCalculations
              });
            }
          }
        } catch (summaryError) {
          // Non-fatal error - UI remains responsive with optimistic data
          console.warn('Background target summary update error:', summaryError);
        }
      }
    } catch (dbError) {
      console.error('Database operation failed:', dbError);
      // Even on error, UI remains responsive
    }
  },
  
  // Delete daily hours with optimistic updates
  deleteDailyHours: async (id: string) => {
    // Store the entry to be deleted in case we need to restore it
    const entryToDelete = get().dailyEntries.find((entry: DailyEntry) => entry.id === id);
    if (!entryToDelete) {
      set({ error: 'Entry not found' });
      return;
    }
    
    // Create a snapshot of calculations before update for proper optimistic UI
    const preUpdateCalculations = { ...get().calculations };
    
    // Optimistically update state and cache
    const updatedEntries = get().dailyEntries.filter((entry: DailyEntry) => entry.id !== id);
    const newCacheEntry = {
      data: updatedEntries,
      timestamp: new Date(),
      isValid: true
    };
    
    // Update UI immediately
    set({ 
      dailyEntries: updatedEntries,
      dailyEntriesCache: newCacheEntry,
      error: null
    });
    
    // Recalculate with optimistic data
    get().calculateTargets();
    
    // Perform database operation in the background
    supabase
      .from('daily_entries')
      .delete()
      .eq('id', id)
      .then(({ error }) => {
        if (error) {
          console.error('Error deleting entry:', error);
          
          // On error, restore the deleted entry
          const revertedEntries = [...get().dailyEntries, entryToDelete];
          
          set({ 
            dailyEntries: revertedEntries,
            dailyEntriesCache: { ...newCacheEntry, data: revertedEntries },
            calculations: preUpdateCalculations as CalculationResults, // Restore original calculations
            error: error.message
          });
          
          // Recalculate with reverted data
          get().calculateTargets();
        }
      });
    
    // Refresh target summary in the background
    setTimeout(() => {
      get().refreshTargetSummary(true)
        .catch((err: any) => console.error('Background refresh error:', err));
    }, 100);
  },
  
  // Core calculation logic as specified in the BRD
  calculateTargets: () => {
    try {
      const profile = get().profile;
      const vacationDays = get().vacationDays;
      const dailyEntries = get().dailyEntries;
      
      if (!profile) {
        return;
      }
      
      // Validate required fields
      if (!profile.tracking_start_date || !profile.tracking_end_date || !profile.work_week_days) {
        return;
      }
      
      // Parse date strings to Date objects
      const startDate = parseISO(profile.tracking_start_date);
      const endDate = parseISO(profile.tracking_end_date);
      const today = new Date();
      
      // Calculate total work days in tracking period
      const totalWorkDays = countWorkDays(
        startDate, 
        endDate, 
        profile.work_week_days,
        vacationDays
      );
      
      // Calculate total hours billed to date
      const totalHoursBilled = dailyEntries.reduce(
        (sum: number, entry: DailyEntry) => sum + entry.billed_hours, 
        0
      );
      
      // Calculate remaining hours needed
      const remainingHoursNeeded = profile.yearly_target_hours - totalHoursBilled;
      
      // Calculate progress percentage
      const progressPercentage = Math.min(
        100,
        (totalHoursBilled / profile.yearly_target_hours) * 100
      );
      
      // Count remaining work days from today
      const remainingWorkDays = countRemainingWorkDays(
        today, 
        endDate, 
        profile.work_week_days,
        vacationDays
      );
      
      // Calculate required average daily hours
      const requiredAverageDailyHours = totalWorkDays > 0 
        ? profile.yearly_target_hours / totalWorkDays 
        : 0;
      
      // Calculate required daily hours for today and future days
      const requiredDailyHoursForToday = remainingWorkDays > 0 
        ? remainingHoursNeeded / remainingWorkDays 
        : 0;
      
      const calculations = {
        totalWorkDays,
        netAvailableWorkDays: totalWorkDays,
        remainingWorkDays,
        requiredAverageDailyHours,
        requiredDailyHoursForToday,
        totalHoursBilled,
        remainingHoursNeeded,
        progressPercentage
      };
      
      set({ calculations });
    } catch (error) {
      console.error('Error calculating targets:', error);
    }
  },
  
  // New method to update exceptions
  refreshTargetExceptions: async () => {
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session.session) return;
      
      const userId = session.session.user.id;
      const vacationDays = get().vacationDays;
      
      // Generate exceptions from vacation days
      const exceptions = generateTargetExceptions(userId, vacationDays);
      
      try {
        // First delete all vacation exceptions
        const { error: deleteError } = await supabase
          .from('target_exceptions')
          .delete()
          .eq('profile_id', userId)
          .eq('reason', 'vacation');
          
        if (deleteError) {
          console.error('Error deleting target_exceptions:', deleteError);
          console.error('Error code:', deleteError.code, 'Error message:', deleteError.message);
        }
        
        // Then insert the new ones if there are any
        if (exceptions.length > 0) {
          // Use upsert instead of insert to handle existing records
          const { error: upsertError } = await supabase
            .from('target_exceptions')
            .upsert(exceptions, {
              onConflict: 'profile_id,exception_date',
              ignoreDuplicates: false // Set to true if you want to ignore rather than update
            });
            
          if (upsertError) {
            console.error('Error upserting target_exceptions:', upsertError);
            console.error('Error code:', upsertError.code, 'Error message:', upsertError.message);
          }
        }
        
        // Update local state
        await get().fetchTargetExceptions();
      } catch (dbError) {
        // Log the full error for easier debugging
        console.error('target_exceptions detailed error:', dbError);
        // Still update local state with the calculated exceptions
        set({ targetExceptions: exceptions as unknown as TargetException[] });
      }
    } catch (error) {
      console.error('Error refreshing target exceptions:', error);
    }
  },
  
  // This method replaces the old refreshDailyTargets
  refreshTargetSummary: async (preferCache = false) => {
    // Prevent concurrent refreshes
    if (get().summaryLoading) return;

    const profile = get().profile;
    if (!profile) return; // Need profile to calculate

    // Option to use cached entries/vacation if preferred (e.g., after optimistic update)
    const entries = preferCache && get().dailyEntriesCache?.isValid ? get().dailyEntriesCache!.data : get().dailyEntries;
    const vacations = preferCache && get().vacationDaysCache?.isValid ? get().vacationDaysCache!.data : get().vacationDays;

    set({ summaryLoading: true, error: null });
    try {
      const summary = calculateTargetSummary(
        profile.tracking_start_date,
        profile.tracking_end_date,
        profile.work_week_days,
        profile.yearly_target_hours,
        vacations,
        entries,
        profile.id
      );

      const { data, error } = await supabase
        .from('target_summaries')
        .upsert(summary, { onConflict: 'profile_id', ignoreDuplicates: false })
        .select()
        .single();

      if (error) throw error;

      const newCacheEntry = {
        data: data as TargetSummary,
        timestamp: new Date(),
        isValid: true
      };
      set({ targetSummary: data as TargetSummary, targetSummaryCache: newCacheEntry });
      console.log('Target summary refreshed and upserted successfully:', data);

    } catch (error: any) {
      console.error('Error refreshing target summary:', error);
      set({ error: error.message });
    } finally {
      set({ summaryLoading: false });
    }
  },

  // To maintain compatibility with the old approach, we'll alias refreshTargetSummary to refreshDailyTargets
  refreshDailyTargets: async () => {
    // Prevent concurrent fetches
    if (get().targetsLoading) return;

    set({ targetsLoading: true, error: null });
    try {
      // ... existing refresh logic using Supabase function ...
      // After successful RPC call, fetch the updated targets
      await get().fetchDailyTargets();
    } catch (error: any) {
      console.error('Error refreshing daily targets:', error);
      set({ error: error.message });
    } finally {
      set({ targetsLoading: false });
    }
  }
}));

// Helper function to count work days between two dates
// (We need to account for vacations here)
function countWorkDays(
  start: Date, 
  end: Date, 
  workWeekDays: number[],
  vacationDays: VacationDay[]
): number {
  let count = 0;
  let current = new Date(start);
  
  // Create a set of vacation dates for quick lookups
  const vacationDateSet = new Set(
    vacationDays.map((vd: VacationDay) => vd.vacation_date)
  );
  
  while (current <= end) {
    const dayOfWeek = current.getDay();
    const dateString = current.toISOString().split('T')[0]; // YYYY-MM-DD
    const isVacationDay = vacationDateSet.has(dateString);
    
    if (workWeekDays.includes(dayOfWeek) && !isVacationDay) {
      count++;
    }
    
    current.setDate(current.getDate() + 1);
  }
  
  return count;
}

// Helper function to count remaining work days from today
function countRemainingWorkDays(
  today: Date, 
  end: Date, 
  workWeekDays: number[],
  vacationDays: VacationDay[]
): number {
  let count = 0;
  let current = new Date(today);
  
  // Create a set of vacation dates for quick lookups
  const vacationDateSet = new Set(
    vacationDays.map((vd: VacationDay) => vd.vacation_date)
  );
  
  while (current <= end) {
    const dayOfWeek = current.getDay();
    const dateString = current.toISOString().split('T')[0]; // YYYY-MM-DD
    const isVacationDay = vacationDateSet.has(dateString);
    
    if (workWeekDays.includes(dayOfWeek) && !isVacationDay) {
      count++;
    }
    
    current.setDate(current.getDate() + 1);
  }
  
  return count;
}

export default useOptimizedUserStore;
